/* global GOOGLE_API_KEY */
export default angular.module('eventix.common.services.googleMaps',[])
/**
 * @ngdoc service
 * @name eventix.common.services.GoogleMapsAPILoader
 * @description
 * A loader service that handles inclusion of the Google Maps JS SDK
 */
    .factory('GoogleMapsAPILoader', function($window, $q, $timeout, LibraryLoader) {
    /**
     * @ngdoc method
     * @name eventix.common.services.GoogleMapsAPILoader#load
     * @methodOf eventix.common.services.GoogleMapsAPILoader
     * @description
     * Load the SDK
     * @returns {Promise<Object>} Resolves with the Google Maps API `window.google.maps` or rejects with a timeout after 5s
     */
        return {
            load: function() {
                let deferred = $q.defer();
                if (_.has($window, 'google.maps'))
                    deferred.resolve($window.google.maps);
                else if(!GOOGLE_API_KEY || !(GOOGLE_API_KEY).length)
                    deferred.reject(new Error('No Google API key found'));
                else {
                    $window.mapsCallback = function() {
                        deferred.resolve($window.google.maps);
                    };
                    let url = 'https://maps.googleapis.com/maps/api/js?key=' + GOOGLE_API_KEY +
                        '&signed_in=true&libraries=places&callback=mapsCallback';
                    LibraryLoader.load(url).catch((error) => deferred.reject(error));
                }
                return deferred.promise;
            }
        };
    })
/**
 * @ngdoc service
 * @name eventix.common.services.TimezoneAPI
 * @description
 * Lookup timezone info for a latitude and longitude at given time
 */
/**
 * @ngdoc method
 * @name eventix.common.services.TimezoneAPI#lookup
 * @methodOf eventix.common.services.TimezoneAPI
 * @description
 * Lookup timezone info for a latitude and longitude at given time
 * @param {Number} latitude Latitude
 * @param {Number} longitude Longitude
 * @param {Integer} timestamp If you want to know DST at lat/lng, supply a Unix timestamp in seconds. Will default to now
 * @param {String} language Language for timezone names, defaults to `en`
 * @return {Object} Promise to deliver output from Google Maps Time Zone API in JSON
 *
 * <pre>
 * {
 *     "dstOffset" : 0,  //Offset for daylight saving time at given timestamp
 *     "rawOffset" : -28800, //Offset from UTC at given location
 *     "status" : "OK",
 *     "timeZoneId" : "America/Los_Angeles", //IANA TZ identifier
 *     "timeZoneName" : "Pacific Standard Time" //Localized name
 * }
 * </pre>
 */
    .factory('TimezoneAPI', function($http, $q) {
        var API = function(latitude, longitude, timestamp = ('' + Date.now()).slice(0,-3), language = 'en') {
            var promise = $http.get('https://maps.googleapis.com/maps/api/timezone/json',{
                params: {
                    location: latitude + ',' + longitude,
                    timestamp: timestamp,
                    language: language,
                    key: GOOGLE_API_KEY
                }
            }).then(res => {
                return $q.resolve(res.data);
            });
            return promise;
        };

        return { lookup: API };
    })
/**
 * @ngdoc directive
 * @name eventix.common.directives:locationAutocomplete
 * @description
 * Augment a form input with location autocompletion using Google Maps API
 *
 * This directive requires an external JS library from Google that is auto-injected when needed. If the file is not loaded, scope will emit an `locationAutocomplete.timeout` and callback will never trigger.
 *
 * Directive ask the user for her location to bias results to her region.
 *
 * @param {Function} callback A function to receive detailed place information
 * @emits locationAutocomplete.timeout
 * @eventType emit
 * @example
 * <pre>
 * <input type="text" ng-model="vm.fullAddress" location-autocomplete callback="vm.autocompleted(place)" />
 * </pre>
 *
 * The callback will receive detailed place information object formatted thus:
 *
 * <pre>
 * {
 *    street_number: 4,
 *    route: 'Privet Drive',
 *    locality: 'Bristol',
 *    country: 'England',
 *    postal_code: 'BS139RN',
 *    location: {
 *        longitude: -2.6061008,
 *        latitude: 51.4108304
 *    }
 * }
 * </pre>
 *
 */
    .directive('locationAutocomplete', function($window, GoogleMapsAPILoader) {
        var API;

        /**
     * Data-format translation table for Google Places response
     */
        var componentForm = {
            street_number: 'short_name',
            route: 'long_name',
            locality: 'long_name', //Google's name for a city
            country: 'long_name',
            postal_code: 'short_name'
        };

        return {
            restrict: 'A',
            scope: {callback: '&', ngModel: '=', locationAutocomplete: '@'},
            link: function(scope, el, attr) {
                var autocomplete;
                var types = _.isEmpty(scope.locationAutocomplete) ? ['geocode'] : scope.locationAutocomplete.split(/[,\s]+/g);
                GoogleMapsAPILoader.load().then(API => {
                    autocomplete = new API.places.Autocomplete(el[0], {types: types });
                    $(el).on('focus', setBounds);
                    autocomplete.addListener('place_changed', function() {
                        var place = autocomplete.getPlace();
                        var result = {};
                        if (!place.geometry)
                            return;
                        for (var i = 0; i < place.address_components.length; i++) {
                            var addressType = place.address_components[i].types[0];
                            if (componentForm[addressType]) {
                                var val = place.address_components[i][componentForm[addressType]];
                                result[addressType] = val;
                            }
                        }
                        result.location = {
                            address: place.formatted_address,
                            latitude: place.geometry.location.lat(),
                            longitude: place.geometry.location.lng()
                        };
                        scope.callback({place: result});
                        scope.$apply(function() {
                            scope.ngModel = result.location.address;
                        });
                    });
                });

                /**
             * Favour Places that are closest to users location
             */
                function setBounds() {
                    if ($window.navigator.geolocation && autocomplete) {
                        $window.navigator.geolocation.getCurrentPosition(function(position) {
                            var geolocation = {
                                lat: position.coords.latitude,
                                lng: position.coords.longitude
                            };
                            var circle = new $window.google.maps.Circle({
                                center: geolocation,
                                radius: position.coords.accuracy
                            });
                            autocomplete.setBounds(circle.getBounds());
                        });
                    }
                }
            }
        };
    })
/**
 * @ngdoc directive
 * @name eventix.common.directives:googleMap
 * @description
 * Component wrapper around a Google Map with markers
 *
 * @param {Object[]} An array of markers
 * @example
 * <pre>
 * <google-map markers="vm.markers"></google-map>
 * </pre>
 */
    .directive('googleMap', function(GoogleMapsAPILoader) {
        var API;
        return {
            restrict: 'E',
            scope: { markers: '=', infoWindow: '&', ctrl: '=?' },
            template: '<div class="googleMap" style="height:400px"></div>',
            link: function(scope, el, attr) {
                let counter = 0;
                scope.mapMarkers = [];
                let googleMap = null;
                let infoWindow = null;
                let firstRender = true;

                scope.ctrl = {
                    openMarker(marker) {
                        let mapMarker = _.find(scope.mapMarkers, { source: marker });
                        if(!mapMarker)
                            return;
                        infoWindow.setContent(scope.infoWindow({ marker: marker }));
                        infoWindow.open(googleMap, mapMarker);
                        var listener = infoWindow.addListener('closeclick', () => {
                            listener.remove();
                            if(marker.icon)
                                mapMarker.setIcon(marker.icon);
                        });
                        if(marker.focusIcon)
                            mapMarker.setIcon(marker.focusIcon);
                        googleMap.setCenter(mapMarker.getPosition());
                        googleMap.setZoom(15);
                    }
                };

                scope.$watch('markers', function(markers) {
                    if(!googleMap) return;
                    drawMarkers(markers);
                }, true);

                function drawMarkers(markers) {
                    markers = markers || scope.markers;
                    _.forEach(scope.mapMarkers, m => m.setMap(null));
                    if(!markers || !markers.length)
                        return;
                    let longitudes = _.map(markers, m => m.longitude);
                    let latitudes = _.map(markers, m => m.latitude);
                    let bounds = {
                        north: _.min(latitudes),
                        east: _.max(longitudes),
                        south: _.max(latitudes),
                        west: _.min(longitudes)
                    };
                    if(firstRender) {
                        googleMap.fitBounds(bounds);
                        firstRender = false;
                    }
                    scope.mapMarkers = _.map(markers, marker => {
                        let markerOptions = {
                            position: {
                                lat: marker.latitude,
                                lng: marker.longitude
                            },
                            map: googleMap,
                            title: marker.name
                        };
                        if(marker.icon)
                            markerOptions.icon = marker.icon;
                        let mapMarker = new API.Marker(markerOptions);
                        mapMarker.source = marker;
                        mapMarker.addListener('click', function() {
                            if(marker.focusIcon)
                                mapMarker.setIcon(marker.focusIcon);
                            infoWindow.setContent(scope.infoWindow({ marker: marker }));
                            infoWindow.open(googleMap, mapMarker);
                            var listener = infoWindow.addListener('closeclick', () => {
                                listener.remove();
                                if(marker.icon)
                                    mapMarker.setIcon(marker.icon);
                            });
                        });
                        return mapMarker;
                    });
                }

                GoogleMapsAPILoader.load().then(_API => {
                    API = _API;
                    googleMap = new API.Map(el.find('.googleMap').get(0));
                    scope.map = googleMap;
                    infoWindow = new API.InfoWindow();
                    drawMarkers();
                });
            }
        };
    }).name;
